import {animate, state, style, transition, trigger} from "@angular/animations";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent, PanelComponent} from "@components";
import {
  ApplicationFormSettings,
  EventItem, FetchRaceByIdRequest,
  FieldConfig,
  Lookups,
  Race, RaceSettings, RegistrationDisplayFlags, TeamType, VisibilityTypes, TOAST_STATE
} from "@model";
import {
  ApplicationFormSettingsService,
  CategoriesService,
  LoaderService,
  RaceService,
  ToastService
} from "@services";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {
  RaceDetailsRegistrationSettingsCustomFieldFormComponent
} from "../race-details-registration-settings-custom-field-form/race-details-registration-settings-custom-field-form.component";
import {take} from "rxjs";
import {isApplicationFormCustomFieldValid} from "@utility";

@Component({
    selector: 'app-race-details-registration-settings',
    templateUrl: './race-details-registration-settings.component.html',
    styleUrls: ['./race-details-registration-settings.component.scss'],
    animations: [
        trigger('fadeInAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(1600)
            ])
        ])
    ],
    standalone: false
})
export class RaceDetailsRegistrationSettingsComponent extends AbstractComponent implements OnInit {

  @Input() applicationFormSettings: ApplicationFormSettings | undefined = new ApplicationFormSettings();
  @Input() fieldConfigTypes: Lookups[] = [];
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() raceSettings: RaceSettings | undefined;
  @Input() disableEditing: boolean = false;
  @Input() visibilityTypes: Lookups[] = [];
  @Input() registrationDisplayFlags: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input() disciplines: Lookups[] = [];
  @Input('sizes') tShirtSizes: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Output() raceChange: EventEmitter<Race | undefined> = new EventEmitter<Race | undefined>();
  @ViewChild("addNewFieldPanel") addNewFieldPanel?: PanelComponent;

  currentApplicationFormSettings: ApplicationFormSettings = ApplicationFormSettings.getApplicationFormSettingsDefault();
  customFields: FieldConfig[] = [];
  genderDisabled: boolean = true;
  disciplineDisabled: boolean = true;
  categoryDisabled: boolean = false;
  birthdayDisabled: boolean = false;
  identityCardFieldsDisabled: boolean = false;

  public isDefaultDataCollapsed: boolean = false;
  public isCustomDataCollapsed: boolean = false;

  errors: { [key: string]: boolean } = {};
  customFieldsValid: boolean[] = [];


  constructor(public translocoService: TranslocoService, private applicationFormSettingsService: ApplicationFormSettingsService,
              private toastService: ToastService, private loaderService: LoaderService, private modalService: NgbModal,
              private raceService:RaceService) {
    super();
  }

  ngOnInit() {
    this.initApplicationSettingsFields();
  }

  private initApplicationSettingsFields() {
    this.currentApplicationFormSettings = this.applicationFormSettings ? {...this.applicationFormSettings} : ApplicationFormSettings.getApplicationFormSettingsDefault();
    this.initDefaultFields();
    if (this.applicationFormSettings?.raceId) {
      this.customFields = this.applicationFormSettings?.customFields ? [...this.applicationFormSettings!.customFields] : []
      this.customFields.forEach((customField:FieldConfig) => {
        this.customFieldsValid.push(isApplicationFormCustomFieldValid(customField, this.event).isValid ?? false);
      })
    }
    if (this.currentApplicationFormSettings.identityCardNumberRegistrationDisplayFlag === RegistrationDisplayFlags.NOT_USED || !this.currentApplicationFormSettings.identityCardNumberRegistrationDisplayFlag) {
     this.disableIdentityCardFields();
    }
    else {
      this.enableIdentityCardFields();
    }
  }

  private initDefaultFields() {
    this.initPredefinedFields();
    this.initGenderFields();
    this.initDisciplineFields();
    this.initCategoryFields();
  }

  private initPredefinedFields(): void {
    this.currentApplicationFormSettings.nameRegistrationDisplayFlag = RegistrationDisplayFlags.ALWAYS_USED;
    this.currentApplicationFormSettings.nameVisibility = VisibilityTypes.ALWAYS_VISIBLE;
    this.currentApplicationFormSettings.nameRequired = true;
    this.currentApplicationFormSettings.lastnameRegistrationDisplayFlag = RegistrationDisplayFlags.ALWAYS_USED;
    this.currentApplicationFormSettings.lastnameVisibility = VisibilityTypes.ALWAYS_VISIBLE;
    this.currentApplicationFormSettings.lastnameRequired = true;
    this.currentApplicationFormSettings.emailRegistrationDisplayFlag = RegistrationDisplayFlags.ALWAYS_USED;
    this.currentApplicationFormSettings.emailVisibility = VisibilityTypes.VISIBLE_TO_ADMINISTRATION;
    this.currentApplicationFormSettings.emailRequired = true;
  }

  private initGenderFields() {
    if (this.raceSettings && this.raceSettings.teamType) {
      if (this.raceSettings.teamType === TeamType.SINGLE) {
        this.currentApplicationFormSettings.genderRegistrationDisplayFlag = RegistrationDisplayFlags.ALWAYS_USED;
        this.currentApplicationFormSettings.genderVisibility = VisibilityTypes.ALWAYS_VISIBLE;
        this.currentApplicationFormSettings.genderRequired = true;
        this.genderDisabled = true;
      } else {
        if (this.raceSettings.genderRequired) {
          this.currentApplicationFormSettings.genderRegistrationDisplayFlag = RegistrationDisplayFlags.ALWAYS_USED;
          this.currentApplicationFormSettings.genderVisibility = VisibilityTypes.ALWAYS_VISIBLE;
          this.currentApplicationFormSettings.genderRequired = true;
          this.genderDisabled = true;
        } else {
          this.currentApplicationFormSettings.genderRegistrationDisplayFlag = RegistrationDisplayFlags.NOT_USED;
          this.currentApplicationFormSettings.genderVisibility = VisibilityTypes.NOT_VISIBLE;
          this.currentApplicationFormSettings.genderRequired = false;
          this.genderDisabled = false;
        }
      }
    } else {
      this.genderDisabled = true;
    }
  }

  private initCategoryFields() {
    if (this.raceSettings && this.raceSettings.category) {
      if (this.raceSettings.category.categoryType === CategoriesService.CATEGORY_NOT_USED_TYPE_ID) {
        this.currentApplicationFormSettings.categoryRegistrationVisibility = VisibilityTypes.NOT_VISIBLE;
        this.categoryDisabled = true;
        this.birthdayDisabled = false;
      } else {
        this.currentApplicationFormSettings.birthdayRequired = true;
        this.currentApplicationFormSettings.birthdayRegistrationDisplayFlag = RegistrationDisplayFlags.ALWAYS_USED;
        this.birthdayDisabled = true;
      }
    }
  }


  private initDisciplineFields() {
    if (this.raceSettings && this.raceSettings) {
      if (this.raceSettings.teamType === TeamType.SINGLE) {
        this.disciplineDisabled = true;
        this.currentApplicationFormSettings.disciplineRequired = false;
        this.currentApplicationFormSettings.disciplineVisibility = VisibilityTypes.NOT_VISIBLE;
        this.currentApplicationFormSettings.disciplines = undefined;
        this.currentApplicationFormSettings.disciplineRegistrationDisplayFlag = RegistrationDisplayFlags.NOT_USED;
      } else {
        if (this.raceSettings.selectionOfTeamMembersType) {
          this.currentApplicationFormSettings.disciplineRegistrationDisplayFlag = this.raceSettings.selectionOfTeamMembersType;
          if (this.currentApplicationFormSettings.disciplineRegistrationDisplayFlag === RegistrationDisplayFlags.ALWAYS_USED) {
            this.currentApplicationFormSettings.disciplineRequired = true;
          }
          this.disciplineDisabled = false;
        }
      }
    } else {
      this.disciplineDisabled = true;
      this.currentApplicationFormSettings.disciplineRequired = false;
      this.currentApplicationFormSettings.disciplineVisibility = VisibilityTypes.NOT_VISIBLE;
      this.currentApplicationFormSettings.disciplines = undefined;
      this.currentApplicationFormSettings.disciplineRegistrationDisplayFlag = RegistrationDisplayFlags.NOT_USED;
    }
  }

  private disableIdentityCardFields(): void {
    this.currentApplicationFormSettings.identityCardNumberRequired = false;
    this.currentApplicationFormSettings.identityCardNumberVisibility = VisibilityTypes.NOT_VISIBLE;
    this.currentApplicationFormSettings.identityCardExpiresRegistrationDisplayFlag = RegistrationDisplayFlags.NOT_USED;
    this.currentApplicationFormSettings.identityCardExpiresVisibility = VisibilityTypes.NOT_VISIBLE;
    this.currentApplicationFormSettings.identityCardExpiresRequired = false;
    this.currentApplicationFormSettings.identityCardIssuerRegistrationDisplayFlag = RegistrationDisplayFlags.NOT_USED;
    this.currentApplicationFormSettings.identityCardIssuerVisibility = VisibilityTypes.NOT_VISIBLE;
    this.currentApplicationFormSettings.identityCardIssuerRequired = false;
    this.identityCardFieldsDisabled = true;
  }

  private enableIdentityCardFields(): void {
    this.identityCardFieldsDisabled = false;
  }


  fieldStatusChanged(hasError: boolean, fieldName: string) {
    this.errors[fieldName] = hasError;
  }

  onCancel(): void {
    this.initApplicationSettingsFields();
  }

  deleteCustomField(index: number) {
    this.customFields.splice(index, 1);
    this.customFieldsValid.splice(index, 1);
  }

  onSave() {
    if (!this.isValid() || this.disableEditing) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.registration.settings.form.error"));
      return;
    } else if (!this.race?.id || !this.currentApplicationFormSettings) {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    } else {
      this.currentApplicationFormSettings.customFields = this.customFields.length > 0 ? this.customFields : undefined;
      this.currentApplicationFormSettings.raceId = this.race.id;
      this.loaderService.startLoading();
      this.applicationFormSettingsService.updateApplicationFormSettings(this.currentApplicationFormSettings, () => {
        this.raceService.findRaceByIdProtected(new FetchRaceByIdRequest(this.race?.id), false)
          .pipe(take(1)).subscribe((race: Race | null) => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("app.common.success.race.registration.settings.edit"));
          this.raceChange.emit(race ?? undefined);
        });
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.common.server.error"))
      });
    }
  }

  isValid() {
    return !Object.values(this.errors).some(value => value) && !this.customFieldsValid.some(value => !value);
  }


  openCustomFieldPanel(customField?: FieldConfig, index?: number) {
    if (!this.disableEditing) {
      const modalRef: NgbModalRef = this.modalService.open(RaceDetailsRegistrationSettingsCustomFieldFormComponent, {
        centered: true,
        windowClass: 'custom-field-modal',
        scrollable: true
      });
      modalRef.componentInstance.race = this.race;
      modalRef.componentInstance.event = this.event;
      modalRef.componentInstance.fieldConfigTypes = this.fieldConfigTypes;
      modalRef.componentInstance.registrationDisplayFlags = this.registrationDisplayFlags;
      modalRef.componentInstance.visibilityTypes = this.visibilityTypes;
      modalRef.componentInstance.isAddForm = !!customField;
      modalRef.componentInstance.customField = customField;
      modalRef.result.then((result: FieldConfig) => {
        const isValid : boolean = isApplicationFormCustomFieldValid(result, this.event).isValid ?? false;
        if (customField && index !== undefined) {
          this.customFields[index] = result
          this.customFieldsValid[index] = isValid;
        } else {
          this.customFields.push(result);
          this.customFieldsValid.push(isValid)
        }
      }, (reason) => {
        if (reason === 'delete') {
          if (index !== undefined) {
            this.deleteCustomField(index)
          }
        }
      });
    }
  }

  identityCardDisplayFlagValueChange(flag: RegistrationDisplayFlags): void {
    if (flag === RegistrationDisplayFlags.NOT_USED || !flag) {
      this.disableIdentityCardFields();
    }
    else {
      this.enableIdentityCardFields();
    }
  }

  protected readonly RegistrationDisplayFlags = RegistrationDisplayFlags;
  protected readonly VisibilityTypes = VisibilityTypes;
}

